import { useState, useCallback, useRef, useMemo, useEffect } from "react"
import type { MapRef } from "react-map-gl/mapbox"
import { debounce } from "lodash"
import { getKaptcherParcelle } from "@/lib/explore/api/kaptcher-data"
import { searchParcelNumber } from "@/lib/explore/api/geo"
import center from "@turf/center"
import { computeDistanceKm } from "@/lib/explore/utils/format-distance"
import type { RecentParcel, ParcelFilter, IParcel } from "@/types/explore"
import { readVersioned, writeVersioned } from "@/lib/explore/utils/versioned-storage"

const RECENT_PARCELS_KEY = "explorerV2_recentParcels"
const RECENT_PARCELS_VERSION = 1

interface ParsedReference {
  codeInsee: string
  section: string
  number: string
}

export const parseParcelReference = (text: string, communeCode?: string): ParsedReference | null => {
  const cleaned = text.toUpperCase().trim().replace(/\s+/g, "")

  // Full IDU (14 chars): 8 digits + 2-char section + 4 digits
  if (/^[0-9]{8}(?:[A-Z]{2}|0[A-Z])[0-9]{4}$/.test(cleaned)) {
    return {
      codeInsee: cleaned.substring(0, 5),
      section: cleaned.substring(8, 10),
      number: cleaned.substring(10, 14),
    }
  }

  // Commune+section+number (11 chars): 5 digits + 2-char section + 4 digits
  if (/^[0-9]{5}(?:[A-Z]{2}|0[A-Z])[0-9]{4}$/.test(cleaned)) {
    return {
      codeInsee: cleaned.substring(0, 5),
      section: cleaned.substring(5, 7),
      number: cleaned.substring(7, 11),
    }
  }

  // Section+number (6 chars): 2-char section + 4 digits
  if (/^(?:[A-Z]{2}|0[A-Z])[0-9]{4}$/.test(cleaned)) {
    if (!communeCode) return null
    return {
      codeInsee: communeCode,
      section: cleaned.substring(0, 2),
      number: cleaned.substring(2, 6),
    }
  }

  return null
}

interface UseParcelSearchOptions {
  communeCode?: string
  mapRef: React.RefObject<MapRef | null>
}

export const useParcelSearch = (options: UseParcelSearchOptions) => {
  const { communeCode, mapRef } = options
  const [searchValue, setSearchValue] = useState("")
  const [searchResults, setSearchResults] = useState<IParcel[]>([])
  const [isSearching, setIsSearching] = useState(false)
  const [filters, setFilters] = useState<ParcelFilter>({
    surfaceMin: "",
    surfaceMax: "",
    ownerType: "Tous",
    siren: "",
  })

  const [recentParcels, setRecentParcels] = useState<RecentParcel[]>(() =>
    readVersioned(RECENT_PARCELS_KEY, RECENT_PARCELS_VERSION, []),
  )

  const communeCodeRef = useRef(communeCode)
  communeCodeRef.current = communeCode

  const abortControllerRef = useRef<AbortController | null>(null)

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      abortControllerRef.current?.abort()
    }
  }, [])

  const addRecentParcel = useCallback((parcel: IParcel) => {
    if (!parcel.center) return
    const props = parcel.properties || ({} as IParcel["properties"])
    const idu = props.idu || ""
    const entry: RecentParcel = {
      idu,
      section: props.section || "",
      number: props.numero || "",
      address: parcel.address || "",
      surface: props.contenance || 0,
      inseeCode: props.code_insee || idu.substring(0, 5),
      commune: props.commune_name || props.nom_com || "",
      center: parcel.center || undefined,
      visitedAt: Date.now(),
    }
    setRecentParcels((prev) => {
      const filtered = prev.filter((r) => r.idu !== entry.idu)
      const updated = [entry, ...filtered].slice(0, 10)
      writeVersioned(RECENT_PARCELS_KEY, RECENT_PARCELS_VERSION, updated)
      return updated
    })
  }, [])

  const removeRecentParcel = useCallback((idu: string) => {
    setRecentParcels((prev) => {
      const updated = prev.filter((r) => r.idu !== idu)
      writeVersioned(RECENT_PARCELS_KEY, RECENT_PARCELS_VERSION, updated)
      return updated
    })
  }, [])

  const searchParcel = useCallback(
    async (text: string) => {
      // Abort any previous in-flight request
      abortControllerRef.current?.abort()
      const controller = new AbortController()
      abortControllerRef.current = controller

      const parsed = parseParcelReference(text, communeCodeRef.current)
      if (!parsed) {
        setSearchResults([])
        setIsSearching(false)
        return
      }

      setIsSearching(true)
      try {
        const results = await searchParcelNumber(parsed.codeInsee, parsed.section, parsed.number)
        // If aborted while awaiting, discard results
        if (controller.signal.aborted) return

        if (!results || results.length === 0) {
          setSearchResults([])
        } else {
          // Sort by proximity to map center
          const map = mapRef.current?.getMap?.() || mapRef.current
          if (map) {
            const mapCenter = (map as unknown as { getCenter: () => { lng: number; lat: number } }).getCenter()
            const sorted = [...results].sort((a, b) => {
              const distA = bboxDistance(a.bbox as number[], mapCenter.lng, mapCenter.lat)
              const distB = bboxDistance(b.bbox as number[], mapCenter.lng, mapCenter.lat)
              return distA - distB
            })
            // Compute center and distance for each result
            const withCenter = sorted.map((r) => {
              const c = center(r.geometry).geometry.coordinates as [number, number]
              return {
                ...r,
                center: c,
                distanceKm: computeDistanceKm([mapCenter.lng, mapCenter.lat], c),
              }
            })
            setSearchResults(withCenter)
          } else {
            const withCenter = results.map((r) => ({
              ...r,
              center: center(r.geometry).geometry.coordinates as [number, number],
            }))
            setSearchResults(withCenter)
          }
        }
      } catch (error) {
        if (!controller.signal.aborted) {
          console.error("Error searching parcel:", error)
          setSearchResults([])
        }
      } finally {
        if (!controller.signal.aborted) {
          setIsSearching(false)
        }
      }
    },
    [mapRef],
  )

  const debouncedSearch = useMemo(() => debounce((text: string) => searchParcel(text), 400), [searchParcel])

  const onSearchValueChange = useCallback(
    (value: string) => {
      setSearchValue(value)
      if (!value.trim()) {
        setSearchResults([])
        setIsSearching(false)
        debouncedSearch.cancel()
      } else {
        debouncedSearch(value)
      }
    },
    [debouncedSearch],
  )

  const clearSearch = useCallback(() => {
    setSearchValue("")
    setSearchResults([])
    setIsSearching(false)
    debouncedSearch.cancel()
  }, [debouncedSearch])

  const searchParcelByIdu = useCallback(async (idu: string) => {
    if (!idu || idu.length < 5) return
    setIsSearching(true)
    try {
      const parcelle = await getKaptcherParcelle(idu)
      if (parcelle) {
        setSearchResults([parcelle as IParcel])
      } else {
        setSearchResults([])
      }
    } catch {
      setSearchResults([])
    } finally {
      setIsSearching(false)
    }
  }, [])

  return {
    searchValue,
    setSearchValue,
    onSearchValueChange,
    searchResults,
    setSearchResults,
    isSearching,
    filters,
    setFilters,
    recentParcels,
    addRecentParcel,
    removeRecentParcel,
    searchParcelByIdu,
    clearSearch,
  }
}

function bboxDistance(bbox: number[], lng: number, lat: number): number {
  if (!bbox || bbox.length < 4) return Infinity
  const cx = (bbox[0] + bbox[2]) / 2
  const cy = (bbox[1] + bbox[3]) / 2
  return Math.sqrt((cx - lng) ** 2 + (cy - lat) ** 2)
}
